/**
 * Axios client configuration with authentication interceptor
 */

import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { ApiError } from '@/types/api';

// Helper function to handle API errors in components
const formatErrorDetail = (detail: unknown): string => {
  if (!detail) return '';

  if (typeof detail === 'string') {
    return detail;
  }

  if (Array.isArray(detail)) {
    return detail
      .map((item) => formatErrorDetail(item))
      .filter(Boolean)
      .join('; ');
  }

  if (typeof detail === 'object') {
    const record = detail as Record<string, unknown>;
    if (typeof record.msg === 'string') {
      return record.msg;
    }
    return JSON.stringify(detail);
  }

  return String(detail);
};

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api/v1',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - add JWT token to all requests
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Get token from localStorage
    const token = localStorage.getItem('auth_token');

    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle errors globally
apiClient.interceptors.response.use(
  (response) => {
    // Return successful response data
    return response;
  },
  (error: AxiosError<ApiError>) => {
    // Handle specific error cases
    if (error.response) {
      const { status, data } = error.response;
      const detailMessage = formatErrorDetail(data?.detail ?? data);
      const errorMessage = detailMessage || error.message;

      switch (status) {
        case 401:
          // Unauthorized - clear token and redirect to login
          localStorage.removeItem('auth_token');
          localStorage.removeItem('user');

          // Only redirect if not already on login page
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
          break;

        case 403:
          // Forbidden - user doesn't have permission
          console.error('Access forbidden:', errorMessage);
          break;

        case 404:
          // Not found
          console.error('Resource not found:', errorMessage);
          break;

        case 422:
          // Validation error
          console.error('Validation error:', errorMessage);
          break;

        case 500:
          // Internal server error
          console.error('Server error:', errorMessage);
          break;

        default:
          console.error(`Error ${status}:`, errorMessage);
      }
    } else if (error.request) {
      // Request made but no response received
      console.error('No response from server:', error.message);
    } else {
      // Error setting up request
      console.error('Request setup error:', error.message);
    }

    return Promise.reject(error);
  }
  );

export const getErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<ApiError>;
    const detail = axiosError.response?.data?.detail;
    const detailMessage = formatErrorDetail(detail);
    if (detailMessage) {
      return detailMessage;
    }
    return axiosError.message || 'An error occurred';
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'An unknown error occurred';
};

// Helper function to check if user is authenticated
export const isAuthenticated = (): boolean => {
  const token = localStorage.getItem('auth_token');
  return !!token;
};

// Helper function to get stored token
export const getToken = (): string | null => {
  return localStorage.getItem('auth_token');
};

// Helper function to set token
export const setToken = (token: string): void => {
  localStorage.setItem('auth_token', token);
};

// Helper function to clear auth data
export const clearAuth = (): void => {
  localStorage.removeItem('auth_token');
  localStorage.removeItem('user');
};

export default apiClient;
